import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Wallet, FileText, Download, Search, Calendar } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Expense categories
const EXPENSE_CATEGORIES = [
  { id: "all", name: "All Categories" },
  { id: "stock_purchase", name: "Stock/Inventory Purchase" },
  { id: "supplier_payment", name: "Supplier Payment" },
  { id: "office_supplies", name: "Office Supplies" },
  { id: "repairs_maintenance", name: "Repairs & Maintenance" },
  { id: "casual_employee", name: "Casual Employee Payment" },
  { id: "other", name: "Other" }
];

const getExpenseCategoryName = (categoryId) => {
  const cat = EXPENSE_CATEGORIES.find(c => c.id === categoryId);
  return cat ? cat.name : categoryId || "Uncategorized";
};

export const PettyCashReport = () => {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState([]);
  const [totals, setTotals] = useState({ transaction_count: 0, total_amount: 0, total_vat: 0, total_excl_vat: 0 });
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);

      const response = await axios.get(`${API}/reports/petty-cash?${params.toString()}`);
      setTransactions(response.data.transactions || []);
      setSummary(response.data.summary_by_category || []);
      setTotals(response.data.totals || { transaction_count: 0, total_amount: 0, total_vat: 0, total_excl_vat: 0 });
    } catch (error) {
      console.error("Error fetching petty cash report:", error);
      toast.error("Failed to load petty cash report");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchReport();
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedCategory("all");
    // Will refetch on next call
    setTimeout(fetchReport, 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `R ${(amount || 0).toFixed(2)}`;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Wallet className="h-8 w-8 text-green-600" />
          <div>
            <h1 className="text-3xl font-bold">Petty Cash Report</h1>
            <p className="text-gray-500">Track and manage petty cash transactions</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="mb-2 block">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-2 block">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-2 block">Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {[...EXPENSE_CATEGORIES].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleFilter} className="bg-green-600 hover:bg-green-700">
                Apply Filters
              </Button>
              <Button variant="outline" onClick={handleClearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{totals.transaction_count}</div>
              <div className="text-sm text-gray-500">Total Transactions</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{formatCurrency(totals.total_amount)}</div>
              <div className="text-sm text-gray-500">Total Amount (Incl VAT)</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">{formatCurrency(totals.total_excl_vat)}</div>
              <div className="text-sm text-gray-500">Total Excl VAT</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">{formatCurrency(totals.total_vat)}</div>
              <div className="text-sm text-gray-500">Total VAT</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary by Category */}
      {summary.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Summary by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                  <TableHead className="text-right">Total (Incl VAT)</TableHead>
                  <TableHead className="text-right">VAT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{getExpenseCategoryName(item.category)}</TableCell>
                    <TableCell className="text-right">{item.count}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.total)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.vat)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Transaction List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Transaction Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No petty cash transactions found for the selected filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transaction #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Cashier</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">VAT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((t, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-mono text-sm">{t.transaction_number}</TableCell>
                      <TableCell className="text-sm">{formatDate(t.created_at)}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">
                          {getExpenseCategoryName(t.expense_category)}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-xs truncate" title={t.petty_cash_description}>
                        {t.petty_cash_description || "-"}
                      </TableCell>
                      <TableCell>{t.cashier}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(t.total)}</TableCell>
                      <TableCell className="text-right text-gray-500">{formatCurrency(t.tax)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PettyCashReport;
